import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: "Privacy Policy — Three Day Launch" },
      { name: "description", content: "How Three Day Launch collects, uses, and protects your information." },
      { property: "og:title", content: "Privacy Policy — Three Day Launch" },
      { property: "og:description", content: "How Three Day Launch collects, uses, and protects your information." },
      { property: "og:url", content: "https://threedaylaunch.com/privacy-policy" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/privacy-policy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last Updated: 4/7/2026</p>

        <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
          <p>This Privacy Policy explains how we collect, use, and protect your information when you visit this website, opt in for a resource, or purchase a product.</p>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Information We Collect</h2>
            <p>We may collect personal information including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Billing information</li>
              <li>IP address</li>
              <li>Device and browser data</li>
              <li>Website activity</li>
              <li>Any information you voluntarily provide</li>
            </ul>
            <p className="mt-3">This information may be collected when you:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Visit this website</li>
              <li>Click on advertisements</li>
              <li>Opt in for a free resource</li>
              <li>Purchase a product</li>
              <li>Join our email list</li>
              <li>Contact us</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Deliver purchased products</li>
              <li>Provide customer support</li>
              <li>Send access details and updates</li>
              <li>Send marketing emails (you may unsubscribe anytime)</li>
              <li>Improve our website and offers</li>
              <li>Measure advertising performance</li>
              <li>Show relevant advertisements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Meta / Facebook Advertising</h2>
            <p>This website uses Meta advertising and tracking tools, including the Meta Pixel.</p>
            <p className="mt-3">These tools allow us to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Track website visits</li>
              <li>Measure ad performance</li>
              <li>Show relevant ads</li>
              <li>Create custom audiences</li>
              <li>Retarget website visitors</li>
              <li>Optimize advertising campaigns</li>
            </ul>
            <p className="mt-3">Meta may collect or receive information from this website and use that information to provide measurement services and targeted ads.</p>
            <p className="mt-3">You may see ads for our products after visiting this website.</p>
            <p className="mt-3">
              You can learn more about Meta's data use here:{" "}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">https://www.facebook.com/privacy/policy/</a>
            </p>
            <p className="mt-3">
              You can control ad preferences here:{" "}
              <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">https://www.facebook.com/adpreferences</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Cookies &amp; Tracking Technologies</h2>
            <p>This website uses cookies and similar tracking technologies to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Understand user behavior</li>
              <li>Improve website performance</li>
              <li>Track conversions</li>
              <li>Support advertising</li>
              <li>Personalize content</li>
            </ul>
            <p className="mt-3">You can disable cookies in your browser settings.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Email Marketing</h2>
            <p>When you opt in or purchase, you may receive:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Product delivery emails</li>
              <li>Account access emails</li>
              <li>Educational content</li>
              <li>Marketing emails</li>
              <li>Promotional offers</li>
            </ul>
            <p className="mt-3">You may unsubscribe at any time using the link in any email.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Third-Party Services</h2>
            <p>We may use trusted third-party tools including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Payment processors</li>
              <li>Email marketing platforms</li>
              <li>Analytics tools</li>
              <li>Advertising platforms (including Meta)</li>
              <li>Course or membership platforms</li>
            </ul>
            <p className="mt-3">These services collect and process data according to their own privacy policies.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Payment Security</h2>
            <p>All payments are processed securely through third-party payment providers. We do not store your full payment details.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Your Privacy Rights</h2>
            <p>You may:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Request access to your data</li>
              <li>Request corrections</li>
              <li>Request deletion</li>
              <li>Unsubscribe from communications</li>
            </ul>
            <p className="mt-3">
              To make a request, contact us at:{" "}
              <a href="mailto:hello@threedaylaunch.com" className="text-primary underline underline-offset-4">hello@threedaylaunch.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Contact</h2>
            <p>If you have questions about this Privacy Policy, contact:</p>
            <p className="mt-3 font-medium">Three Day Launch</p>
          </section>
        </div>
      </main>

      <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10 mt-16">
        <div className="mx-auto max-w-7xl px-6 text-center text-sm text-white/80">
          © {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.
        </div>
      </footer>
    </div>
  );
}
